import type { MerchantTheme } from "@sirdab/returns-shared";

/**
 * Merchant theming. The merchant can set a logo, a primary-action colour, and (as of
 * this feature) the page background itself — solid or a two-stop gradient. Each is
 * independently optional; the foundation defaults apply wherever a merchant hasn't set
 * one. Earlier guidance here said the brand colour should never touch the page ground
 * ("a logistics receipt, not a marketing moment") — that constraint was deliberately
 * lifted for background theming specifically, while --primary's role (the action only)
 * is unchanged.
 *
 * In foundation terms: this portal points `--primary` at the merchant's colour
 * (COLORS.md, "A brand-filled primary"), and `--page-background`/`--page-ink` at the
 * merchant's page-background choice. With no merchant value for either, the override is
 * REMOVED and the foundation default applies; nothing here hardcodes a default hue.
 */

export interface ResolvedTheme {
  /** `R G B` triple of the merchant's colour, or null for the foundation's ink default. */
  brandRgb: string | null;
  /** Black or white ink that stays legible on the brand colour; null with no colour. */
  brandContrastRgb: string | null;
  logoUrl: string | null;
  /** A valid CSS `background` value (solid colour or 135deg gradient), or null for no override. */
  pageBackgroundCss: string | null;
  /** Black or white ink that stays legible on pageBackgroundCss; null with no override. */
  pageInkRgb: string | null;
}

/** `#1a2b3c` / `#1a2` / `rgb(1,2,3)` -> `26 43 60`. Returns null on anything unparseable. */
export function parseColorToRgbTriple(
  value: string | null | undefined,
): string | null {
  if (typeof value !== "string") return null;
  const raw = value.trim();

  const hex = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(raw);
  if (hex) {
    const digits = hex[1]!;
    const full =
      digits.length === 3
        ? digits
            .split("")
            .map((c) => c + c)
            .join("")
        : digits;
    const n = Number.parseInt(full, 16);
    return `${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255}`;
  }

  const rgb = /^rgba?\(\s*(\d{1,3})[\s,]+(\d{1,3})[\s,]+(\d{1,3})/i.exec(raw);
  if (rgb) {
    const channels = [rgb[1], rgb[2], rgb[3]].map((c) => Number(c));
    if (channels.every((c) => Number.isInteger(c) && c >= 0 && c <= 255)) {
      return channels.join(" ");
    }
  }
  return null;
}

/** Relative luminance, so a merchant with a pale brand colour still gets legible ink. */
export function contrastInkFor(rgbTriple: string): string {
  const [r = 0, g = 0, b = 0] = rgbTriple.split(" ").map(Number);
  const channel = (c: number): number => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  const luminance =
    0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
  return luminance > 0.45 ? "17 17 17" : "255 255 255";
}

/**
 * `logoUrl` is validated as a URL by the shared schema, but a broken image must never
 * block the flow (docs/08) — `<MerchantHeader>` renders the name if the image fails.
 */
export function resolveTheme(
  theme: MerchantTheme | null | undefined,
): ResolvedTheme {
  const brandRgb = parseColorToRgbTriple(theme?.brandColor);
  const bgStartRgb = parseColorToRgbTriple(theme?.backgroundColor);
  const bgEndRgb = parseColorToRgbTriple(theme?.backgroundGradientTo);
  const pageBackgroundCss = resolvePageBackgroundCss(bgStartRgb, bgEndRgb);
  const pageInkRgb =
    pageBackgroundCss === null
      ? null
      : contrastInkFor(
          averageRgb(bgEndRgb ? [bgStartRgb, bgEndRgb] : [bgStartRgb]),
        );

  return {
    brandRgb,
    brandContrastRgb: brandRgb === null ? null : contrastInkFor(brandRgb),
    logoUrl: theme?.logoUrl ?? null,
    pageBackgroundCss,
    pageInkRgb,
  };
}

/**
 * Resolution rules for the three states a merchant's two background fields can be in:
 * - neither set: no override (null) — the foundation default stands.
 * - only start set: solid colour.
 * - both set: a fixed 135deg gradient from start to end (direction is not configurable
 *   in this iteration).
 * - only end set (no start): treated as if end were the solid colour, so clearing just
 *   the start field while an end colour remains still gives a sensible single-colour
 *   result rather than silently reverting to the default.
 */
export function resolvePageBackgroundCss(
  startRgb: string | null,
  endRgb: string | null,
): string | null {
  if (startRgb && endRgb) {
    return `linear-gradient(135deg, rgb(${startRgb}), rgb(${endRgb}))`;
  }
  if (startRgb) return `rgb(${startRgb})`;
  if (endRgb) return `rgb(${endRgb})`;
  return null;
}

function averageRgb(triples: Array<string | null>): string {
  const valid = triples
    .filter((t): t is string => t !== null)
    .map((t) => t.split(" ").map(Number));
  const channel = (i: number) =>
    Math.round(valid.reduce((sum, c) => sum + c[i]!, 0) / valid.length);
  return `${channel(0)} ${channel(1)} ${channel(2)}`;
}

/** The foundation tokens the portal overrides when a merchant colour is present. */
export const THEME_CSS_PROPERTIES = [
  "--primary",
  "--primary-hover",
  "--primary-active",
  "--primary-foreground",
] as const;

/**
 * The custom properties to set on `<html>`, or null when the foundation default stands.
 * Hover and active step the fill toward black the way the ink ladder steps toward gray,
 * so a merchant supplies one value and still gets the full button state machine.
 */
export function themeCssVars(
  theme: ResolvedTheme,
): Record<(typeof THEME_CSS_PROPERTIES)[number], string> | null {
  if (theme.brandRgb === null || theme.brandContrastRgb === null) return null;
  const fill = `rgb(${theme.brandRgb})`;
  return {
    "--primary": fill,
    "--primary-hover": `color-mix(in srgb, ${fill} 88%, black)`,
    "--primary-active": `color-mix(in srgb, ${fill} 78%, black)`,
    "--primary-foreground": `rgb(${theme.brandContrastRgb})`,
  };
}
